/**
 * PlaybooksTab — Playbooks organized by category, fetched from API with hardcoded fallback.
 *
 * Each playbook launches a new chat session with a detailed prompt that
 * instructs Trendy to execute multiple tools in sequence. Playbooks are
 * the key differentiator — they turn "ask a question" into "run a workflow".
 */

import { useState, useEffect, useCallback } from 'react'
import {
  ChevronRight, Plus, X, Play,
  BarChart3, AlertCircle, Factory, Wallet, TrendingUp, ClipboardList,
  Target, Package, User, ShieldAlert, DollarSign, Inbox, BarChart2,
  Search, Truck, FileText, Link2, Mail, CalendarDays,
  CreditCard, Ship, Receipt,
} from 'lucide-react'

// ── Map icon keys to Lucide components ──
const ICON_MAP = {
  'bar-chart': BarChart3,
  'circle-alert': AlertCircle,
  'factory': Factory,
  'wallet': Wallet,
  'trending-up': TrendingUp,
  'clipboard-list': ClipboardList,
  'target': Target,
  'package': Package,
  'user': User,
  'shield-alert': ShieldAlert,
  'dollar-sign': DollarSign,
  'inbox': Inbox,
  'bar-chart-2': BarChart2,
  'search': Search,
  'truck': Truck,
  'file-text': FileText,
  'link-2': Link2,
  'mail': Mail,
  'calendar-days': CalendarDays,
  'credit-card': CreditCard,
  'ship': Ship,
  'receipt': Receipt,
}

// ── Category display labels ──
const CATEGORY_LABELS = {
  daily_ops: 'Daily Operations',
  invoicing: 'Invoicing',
  financial: 'Financial',
  sales: 'Sales',
  customer_intelligence: 'Customer Intelligence',
  purchasing_inventory: 'Purchasing & Inventory',
  shipping_fulfillment: 'Shipping & Fulfillment',
  cross_system: 'Cross-System',
}

// ── Tier badge styling ──
const TIER_STYLES = {
  system: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  admin: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  personal: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
}

// ── Hardcoded fallback playbooks (original 19) ──
const FALLBACK_PLAYBOOKS = [
  {
    id: 'fb-morning-digest', name: 'Morning Digest', category: 'daily_ops', tier: 'system', icon: 'bar-chart',
    description: 'Daily snapshot of alerts, late jobs, cash, and expiring quotes',
    prompt: `Run my morning operations digest. I need a comprehensive snapshot of today's business:\n\n1. First, check AR alerts for any overdue invoices or large balances\n2. Check for late jobs past their due date\n3. Get the daily cash position showing AR aging and AP due\n4. Check for quotes expiring in the next 14 days\n\nPresent the results as an executive summary with sections for each area. Flag anything critical in bold. Include total dollar amounts and counts for each category.`,
  },
  {
    id: 'fb-collections-review', name: 'Collections Review', category: 'daily_ops', tier: 'system', icon: 'circle-alert',
    description: 'Prioritize overdue invoices and find who to contact',
    prompt: `Run a collections review. I need to prioritize our AR follow-ups:\n\n1. Check AR alerts for overdue invoices (flag anything 30+ days overdue)\n2. Search for all open invoices that are past due\n3. For the top 5 largest overdue balances, get the customer AR aging breakdown\n4. For those same customers, pull their contact information so I know who to call\n\nPresent as a prioritized collections list with customer name, total overdue amount, aging breakdown, days overdue, and contact details. Sort by largest balance first.`,
  },
  {
    id: 'fb-production-status', name: 'Production Status', category: 'daily_ops', tier: 'system', icon: 'factory',
    description: 'See late jobs, cost overruns, and shop floor capacity',
    prompt: `Give me a full production status report:\n\n1. Check for late jobs past their due date — show all of them sorted by days late\n2. Check for job overruns where actual costs exceed estimates (threshold 90%)\n3. Get production capacity utilization across resource groups\n\nPresent as three sections: Late Jobs (with severity), Cost Overruns (with dollar impact at $140/hr burden rate), and Capacity Utilization. Flag anything critical.`,
  },
  {
    id: 'fb-cash-position', name: 'Cash Position', category: 'financial', tier: 'system', icon: 'wallet',
    description: 'Current cash, projected cash flow, and collection opportunities',
    prompt: `Run a complete cash position analysis:\n\n1. Get the daily cash position with AR aging buckets and AP due\n2. Search for overdue AR invoices to identify collection opportunities\n3. Check for customers on or near credit hold\n\nPresent as a CFO dashboard: current cash position, 7-day and 14-day net projections, AR aging breakdown with percentages, AP obligations coming due, and any credit risk flags. Include dollar amounts for everything.`,
  },
  {
    id: 'fb-margin-analysis', name: 'Margin Analysis', category: 'financial', tier: 'system', icon: 'trending-up',
    description: 'True job profitability using actual burden rates',
    prompt: `Run a margin analysis using our true cost rates:\n\n1. Get margin by job analysis with the adjusted $140/hr burden rate\n2. Get customer profitability rankings from recent invoices\n3. Check for job overruns that are eating into margins\n\nShow the results as: Top/Bottom 10 jobs by true margin percentage, customer profitability rankings, and any active jobs currently over budget. Highlight the gap between Epicor-reported margins and true margins.`,
  },
  {
    id: 'fb-ap-payment-run', name: 'AP Payment Run', category: 'financial', tier: 'system', icon: 'clipboard-list',
    description: 'Plan vendor payments with priority and discount opportunities',
    prompt: `Prepare an AP payment run analysis:\n\n1. Get the daily cash position to see what we have available\n2. Search for overdue invoices on the AR side (money coming in)\n3. Check AR alerts for any large receivables we can expect soon\n\nPresent as a payment planning dashboard: available cash, AR expected in 7 days, recommended payment prioritization (past due first, then discount opportunities, then upcoming). Include total amounts for each category.`,
  },
  {
    id: 'fb-quote-followup', name: 'Quote Follow-up', category: 'sales', tier: 'system', icon: 'target',
    description: 'Find expiring quotes and prioritize follow-ups by value',
    prompt: `Run a sales quote follow-up report:\n\n1. Check for quotes expiring in the next 30 days — show all with potential value\n2. Check for quotes expiring in the next 7 days as urgent follow-ups\n3. Get customer profitability for any customers with expiring quotes so I can prioritize high-value relationships\n\nPresent as a prioritized follow-up list with: quote number, customer name, expiration date, quote value, and customer historical revenue. Sort by expiration date (most urgent first). Flag quotes over $10,000 as high priority.`,
  },
  {
    id: 'fb-sales-backlog', name: 'Sales Backlog', category: 'sales', tier: 'system', icon: 'package',
    description: 'Open orders, upcoming shipments, and at-risk deliveries',
    prompt: `Run a sales backlog and shipping forecast:\n\n1. Get the full sales backlog of open orders grouped by customer\n2. Search for orders with ship dates in the next 14 days\n3. Check for late jobs that might delay upcoming shipments\n\nPresent as three sections: Backlog Summary (total value, order count, top customers by backlog), Shipping This Week/Next Week (with order details and readiness), and At-Risk Shipments (orders tied to late jobs). Include dollar amounts and dates.`,
  },
  {
    id: 'fb-customer-360', name: 'Customer 360°', category: 'customer_intelligence', tier: 'system', icon: 'user',
    description: 'Complete customer profile with credit, orders, and contacts',
    prompt: `I need a full 360° view of our top 5 customers by AR balance. For each customer:\n\n1. Get their credit status (credit limit, available credit, utilization %)\n2. Get their AR aging summary with aging buckets\n3. Get their recent order history (last 10 orders)\n4. Get their contact information\n\nPresent as a detailed customer profile card for each: name, credit status (good/warning/hold), AR total and aging breakdown, recent order activity (count + total value), and primary contact. Rank by AR balance highest first.`,
  },
  {
    id: 'fb-credit-risk', name: 'Credit Risk Review', category: 'customer_intelligence', tier: 'system', icon: 'shield-alert',
    description: 'Spot customers near or over credit limits before they order',
    prompt: `Run a credit risk review across all customers:\n\n1. Check for customers on credit hold or near their credit limit\n2. For any flagged customers, get their AR aging breakdown\n3. For customers near the limit (>80% utilization), get their open order backlog to see if new orders will push them over\n\nPresent as a risk dashboard with three tiers: CRITICAL (on hold or over limit), WARNING (>80% utilization), and WATCH (>60% utilization). Include credit limit, current AR, utilization %, and open order pipeline for each.`,
  },
  {
    id: 'fb-price-audit', name: 'Price List Audit', category: 'customer_intelligence', tier: 'system', icon: 'dollar-sign',
    description: 'Compare customer prices against costs to find margin leaks',
    prompt: `Run a pricing audit for our top 10 customers by revenue:\n\n1. Search customers sorted by order value\n2. For each of the top 10, get their customer-specific price list\n3. For the parts on their price list, get the current part costs\n\nPresent as a pricing matrix showing: customer name, part number, customer price, our standard cost, and calculated margin %. Flag any items with margin below 20% as "LOW MARGIN" and anything below 10% as "CRITICAL — REPRICE". Sort by lowest margin first.`,
  },
  {
    id: 'fb-po-status', name: 'PO Status Review', category: 'purchasing_inventory', tier: 'system', icon: 'inbox',
    description: 'Track open POs, expected receipts, and vendor issues',
    prompt: `Run a purchase order status review:\n\n1. Search for all open purchase orders\n2. Get release schedules for the top 10 POs by value to see what's expected\n3. Check for any uninvoiced receipts that need AP processing\n4. Check AP alerts for any vendor payment issues\n\nPresent as a purchasing dashboard: Open PO Summary (count, total value, top vendors), Expected Receipts This Week/Next Week, Uninvoiced Receipts needing attention, and any vendor payment flags. Include dollar amounts.`,
  },
  {
    id: 'fb-inventory-health', name: 'Inventory Health', category: 'purchasing_inventory', tier: 'system', icon: 'bar-chart-2',
    description: 'Find stockouts, excess inventory, and unusual transactions',
    prompt: `Run an inventory health check:\n\n1. Search for manufactured parts and check their inventory levels\n2. Search for purchased parts and check their inventory levels\n3. Look at recent inventory transactions in the last 7 days to spot unusual activity\n\nPresent as an inventory dashboard showing: parts with zero on-hand that have open orders against them, parts with high inventory but no recent demand, and a transaction summary (receipts vs issues vs adjustments in the last 7 days). Flag any anomalies.`,
  },
  {
    id: 'fb-vendor-scorecard', name: 'Vendor Scorecard', category: 'purchasing_inventory', tier: 'system', icon: 'search',
    description: 'Rate vendors by payment status, holds, and AP aging',
    prompt: `Run a vendor scorecard analysis:\n\n1. Check AP alerts for past due invoices, large balances, and payment issues\n2. Search for any vendors on hold or payment hold\n3. Get AP aging reports for the top 5 vendors by AP balance\n4. Get AP invoices due in the next 14 days\n\nPresent as a vendor management dashboard: Vendor Risk Summary (on hold, past due), AP Aging by Top Vendors, and Upcoming Payment Obligations. Include recommended actions for each vendor. Sort by urgency.`,
  },
  {
    id: 'fb-shipping-forecast', name: 'Shipping Forecast', category: 'shipping_fulfillment', tier: 'system', icon: 'truck',
    description: 'See what ships this week and flag anything not ready',
    prompt: `Run a shipping forecast for the next 7 days:\n\n1. Get all order releases with ship dates in the next 7 days\n2. For each release, check if the linked job is complete and ready to ship\n3. Search for any recent shipments in the last 3 days to see what already went out\n\nPresent as a shipping calendar: Today, Tomorrow, and each day this week. For each day show the orders, customers, quantities, and readiness status (Ready to Ship / Job In Progress / Job Late). Flag anything that's supposed to ship but the job isn't complete.`,
  },
  {
    id: 'fb-uninvoiced-shipments', name: 'Uninvoiced Shipments', category: 'shipping_fulfillment', tier: 'system', icon: 'file-text',
    description: 'Find shipped orders that haven\'t been billed yet',
    prompt: `Find all shipments that have been shipped but not yet invoiced:\n\n1. Search for uninvoiced shipments\n2. For each, get the related order details to see the expected invoice value\n3. Get the customer AR balance for affected customers\n\nPresent as a billing action list: shipment number, customer, ship date, days since shipped, estimated invoice value, and customer's current AR balance. Sort by oldest shipment first. Sum the total uninvoiced revenue at the top.`,
  },
  {
    id: 'fb-crm-erp-sync', name: 'CRM ↔ ERP Sync Check', category: 'cross_system', tier: 'system', icon: 'link-2',
    description: 'Find customers in Epicor missing from HubSpot CRM',
    prompt: `Run a CRM-to-ERP sync check:\n\n1. Search for the top 20 Epicor customers by recent order value\n2. For each customer, search HubSpot CRM for a matching company by name\n3. Flag any Epicor customers that don't have a matching HubSpot company\n\nPresent as a sync status report: Matched (customer exists in both systems with details), Unmatched (in Epicor but missing from HubSpot — needs CRM entry), and a summary count.`,
  },
  {
    id: 'fb-email-activity', name: 'Customer Email Activity', category: 'cross_system', tier: 'system', icon: 'mail',
    description: 'See which top customers you\'ve emailed recently and who needs outreach',
    prompt: `Run a customer communication audit:\n\n1. Search Outlook emails from the last 7 days for any customer-related messages\n2. Get the top 10 customers by open AR balance from Epicor\n3. Cross-reference: which of our top AR customers have we communicated with recently?\n\nPresent as a communication heat map: customers with high AR and recent email activity (good — following up), customers with high AR but no recent emails (GAP — needs outreach), and a summary of email volume this week.`,
  },
  {
    id: 'fb-meeting-prep', name: 'Meeting Prep', category: 'cross_system', tier: 'system', icon: 'calendar-days',
    description: 'Pull ERP data for upcoming customer meetings automatically',
    prompt: `Help me prepare for upcoming customer meetings:\n\n1. Search my Outlook calendar for meetings in the next 3 days\n2. For any meetings that appear to be customer-related, search Epicor for the customer\n3. For matched customers, get their credit status, recent orders, and AR aging\n\nPresent as meeting prep cards: meeting title, date/time, attendees, and then the customer's ERP snapshot (credit status, last 5 orders, current AR balance, any overdue invoices).`,
  },
  // ── Invoicing Playbooks (from Claude Desktop skills — exact workflow match) ──
  // requires_write: true → hidden for read-only (team) users
  {
    id: 'fb-deposit-invoice', name: 'Create Deposit Invoices', category: 'invoicing', tier: 'system', icon: 'credit-card',
    description: 'Find orders needing deposits and create DEP invoices', requires_write: true,
    prompt: `Run the deposit invoice workflow.\n\n**Step 1 — Find Pending Orders:**\nQuery the \`AR-DepositDash\` BAQ to find orders needing deposit invoices. Orders with deposit-required terms (DBS1, etc.) where \`InvcHead1_InvoiceNum = NULL\` means no deposit invoice exists yet. The BAQ filters by \`ReadyToCalc = true\` on the sales order.\n\n**Step 2 — Show Summary Table (DRY RUN):**\nFor each pending order, show: Order #, Customer, PO #, Order Total, 50% Deposit Amt, Terms Code\n\n⛔ **STOP HERE.** Do NOT proceed to Step 3 until the user explicitly says to proceed. Ask: 'These are the orders pending deposit invoices. Which ones should I create invoices for? Say **proceed with all**, pick specific order numbers, or **cancel**.'\n\n**Step 3 — Create Invoices (ONLY after explicit user approval):**\nFor each order I approve, create a DEP invoice using the Epicor ARInvoiceSvc API chain:\n1. \`GetNewInvcHead\` with groupID (YYYYMMDD format, PST timezone), custNum, invoiceType='DEP'\n2. \`OnChangeofOrderNum\` to link invoice to sales order — this populates Sold To, PO, Terms, Sales Rep, Plant\n3. \`Update\` to save and get the invoice number\n\nAuto-populated fields: Invoice Type (DEP), Sales Order, Bill To/Sold To, PO Number, Terms Code, Sales Rep, Plant (MfgSys), Invoice/Apply/Ship Date (today PST), Due Date (today + 10 days), FOB (ORIGIN), Fiscal Year/Period, GroupID (YYYYMMDD).\n\n**Step 4 — Post-Creation Instructions:**\nAfter creating each invoice, show the invoice number and remind me:\n1. Open the invoice in Epicor AR Invoice Entry\n2. **Enter the Extended Price** (deposit amount, typically 50% of order total) on the DEPOSIT line\n3. Run **"Set All Ready to Calculate"** from the Actions menu\n4. Run **Group Edit List** to validate before posting\n\n⚠️ CRITICAL: Extended Price (deposit amount) CANNOT be set via API — Epicor's business logic requires UI entry. The API creates a complete invoice shell; user enters the amount manually. Also: 'Set All Ready to Calculate' MUST be run before Group Edit List will work.`,
  },
  {
    id: 'fb-shipping-invoice', name: 'Create Shipping Invoices', category: 'invoicing', tier: 'system', icon: 'ship',
    description: 'Find shipped orders needing invoices and create them from pack slips', requires_write: true,
    prompt: `Run the shipping invoice workflow.\n\n**Step 1 — Find Uninvoiced Shipments:**\nQuery the \`TEMP_ShippedLinesDash\` BAQ to find closed orders with remaining balance. This BAQ filters: \`OrderHed_OpenOrder = false\` AND \`Calculated_CalcRemBal > 0\` — showing orders that have shipped but are not fully invoiced.\n\n**Step 2 — Document Screening:**\nFor each order found in Step 1, check the order's attachment tree using \`epicor_get_attachments\` (or query \`Ice.BO.AttachmentSvc/Attachments\` with RelatedToFile='OrderHed' and Key1=OrderNum). Screen file names and DocTypeIDs for:\n- **CPO** (Customer Purchase Order) — DocTypeID = 'CPO'\n- **SHIP** (Shipping docs / BOL) — DocTypeID = 'SHIP'\n\nShow a table with: Order #, Customer, CPO (✅/❌), SHIP (✅/❌), Remaining Balance\nFlag any orders MISSING CPO or SHIP documents — these may need attention before invoicing.\n\n**Step 3 — Show Summary Table (DRY RUN):**\nFor each pending shipment, show: Pack #, Order #, Customer, Ship Date, Days Since Shipped, Remaining Balance, CPO Status, SHIP Status\nSort by oldest shipment first. Sum the total uninvoiced revenue at the top.\n\n⛔ **STOP HERE.** Do NOT proceed to Step 4 until the user explicitly says to proceed. Ask: 'These shipments have outstanding balances. Which ones should I create invoices for? Say **proceed with all**, pick specific pack numbers, or **cancel**. Note: orders flagged missing CPO/SHIP docs may need those attached first.'\n\n**Step 4 — Create Invoices (ONLY after explicit user approval):**\nFor each approved pack slip, create a SHP invoice using \`epicor_create_shipment_invoice\` with the pack number. The tool handles the full API chain:\n1. Ensures AR Invoice Group exists (GroupID = YYYYMMDD, PST timezone)\n2. Calls \`ARInvoiceSvc/GetShipments\` with GroupID, CustList, PackSlips, Plant (MfgSys), BillToFlag, overBillDay\n3. Invoices are auto-created with correct lines, quantities, and prices from the pack slip\nProcess each pack slip individually — do NOT batch multiple packs in one call.\n\n**Key difference from DEP invoices:** SHP invoices auto-calculate amounts from shipment data — NO manual entry needed!\n\n**Step 5 — Post-Creation Instructions:**\nAfter creating invoices, remind me:\n1. Open AR Invoice Entry in Epicor\n2. Select the Group (YYYYMMDD format)\n3. Run **"Set All Ready to Calculate"** from Actions menu\n4. Run **Group Edit List** to validate\n5. Post when ready`,
  },
  {
    id: 'fb-proforma-invoice', name: 'Pro-Forma Invoice', category: 'invoicing', tier: 'system', icon: 'receipt',
    description: 'Generate a balance reconciliation and pro-forma PDF for an order', requires_write: true,
    prompt: `Run the pro-forma invoice workflow.\n\n**Ask me which sales order(s) to process.** I may provide one or multiple order numbers, or say 'check my Pro-Forma folder' to search the Outlook Pro-Forma folder for team requests.\n\n**For each order, run this workflow:**\n\n**Step 1 — Order Validation:**\nFetch the full order details using epicor_get_order. Show the order header: customer, PO number, order total, terms, and ship-to. Determine taxability: check \`OTSTaxRegionCode\`, \`TaxRegionCode\`, and existing \`TotalTax\`. Order is taxable if ANY tax region is populated OR TotalTax > 0.\n\n**Step 2 — Tax Calculation (if taxable):**\nFor taxable orders, call \`Erp.BO.SalesOrderSvc/SetReadyToCalc\` to trigger tax calculation, then refresh the order to get the updated tax amount. Skip for tax-exempt orders.\n\n**Step 3 — Balance Reconciliation:**\nBuild a complete financial picture using these CData Connect AI queries:\n- **Lines**: Query \`zES_SalesOrderLines\` for line-by-line detail\n- **Misc Charges**: Query \`zIWOrderHedMsc\` for freight, tariffs, etc.\n- **Invoices**: Query \`zES_Invoice\` for all invoices against this order\n- **Payments**: Query \`zCashDtl\` for cash receipts by invoice\n\nPresent the reconciliation in this exact format:\n\`\`\`\n═══════════════════════════════════════════════════════════════════\nSO {OrderNum} COMPLETE BALANCE RECONCILIATION\n═══════════════════════════════════════════════════════════════════\nCustomer: {Name}\nPO Number: {PONum}\n\nLINES SUBTOTAL:                                        \${amount}\nMISC CHARGES:                                          \${amount}\nSALES TAX:                                             \${amount}\n───────────────────────────────────────────────────────────────────\nORDER TOTAL:                                           \${amount}\n\nINVOICES ISSUED:\n  Invoice #\${num} (\${date}) - \${type}                    (\${amount})\n\nPAYMENTS RECEIVED:\n  \${method} (\${date})                                   (\${amount})\n───────────────────────────────────────────────────────────────────\nBALANCE DUE:                                           \${amount}\n═══════════════════════════════════════════════════════════════════\n\`\`\`\n\n**Step 4 — Approval (DRY RUN GATE):**\n⛔ **STOP HERE.** Do NOT proceed to Step 5 until the user explicitly approves. Ask: 'Does this balance reconciliation look correct? Say **approved** to generate the pro-forma PDF, or let me know what needs adjustment.'\n\n**Step 5 — Generate Pro-Forma PDF (ONLY after explicit user approval):**\nCall \`Erp.Rpt.ProFormaInvcSvc/SubmitToAgent\` with Report Style 1001 (Amtrend Standard) and routing enabled. This will:\n1. Email the PDF to waseem@amtrend.com\n2. Attach the PDF to the Sales Order as document type ARINV\n3. Generate a print preview\n\nAfter generation, verify the attachment was created using the epicor-attachments tools.\n\nIf I provide multiple orders, process each sequentially with approval per order, then provide a final summary table of all processed orders.`,
  },
]

// ── Category dropdown options for create modal ──
const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))

/**
 * Create Playbook Modal
 */
function CreatePlaybookModal({ onClose, onCreated }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('daily_ops')
  const [prompt, setPrompt] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = async () => {
    if (!name.trim() || !prompt.trim()) return
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('amtrend_token')
      const res = await fetch('/api/playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ name: name.trim(), description: description.trim(), category, prompt: prompt.trim(), tier: 'personal' }),
      })
      if (!res.ok) throw new Error('Failed to create playbook')
      const created = await res.json()
      onCreated(created)
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-surface-dark-raised rounded-xl shadow-xl w-full max-w-md mx-4 p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">New Playbook</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekly Vendor Review"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-surface-dark-overlay text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short summary of what this playbook does"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-surface-dark-overlay text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-surface-dark-overlay text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/40"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              placeholder="The detailed instructions Trendy will follow when running this playbook..."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-surface-dark-overlay text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amtrend-blue/40 resize-none"
            />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !prompt.trim()}
            className="px-4 py-1.5 text-sm font-medium text-white bg-amtrend-blue hover:bg-amtrend-blue-dark rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Group playbooks by category, preserving category order
 */
function groupByCategory(playbooks) {
  const groups = {}
  for (const pb of playbooks) {
    const cat = pb.category || 'other'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(pb)
  }
  // Sort by known category order, then unknowns at end
  const knownOrder = Object.keys(CATEGORY_LABELS)
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const ai = knownOrder.indexOf(a)
    const bi = knownOrder.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
  return sortedKeys.map((key) => ({
    label: CATEGORY_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    items: groups[key],
  }))
}

// Roles that can see write-dependent playbooks (invoicing, etc.)
const WRITE_ROLES = new Set(['admin', 'executive', 'plant_manager'])

export default function PlaybooksTab({ onStartChat, userRole }) {
  const canWrite = WRITE_ROLES.has(userRole)
  const [playbooks, setPlaybooks] = useState(FALLBACK_PLAYBOOKS)
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)

  // Fetch playbooks from API on mount
  useEffect(() => {
    let cancelled = false
    async function fetchPlaybooks() {
      try {
        const token = localStorage.getItem('amtrend_token')
        const res = await fetch('/api/playbooks', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          // Tag DB playbooks in write-dependent categories
          const WRITE_CATEGORIES = new Set(['invoicing', 'Invoicing'])
          const tagged = data.map(p =>
            WRITE_CATEGORIES.has(p.category) ? { ...p, requires_write: true } : p
          )
          // Merge in any hardcoded playbooks not already in the API response
          const apiIds = new Set(tagged.map(p => p.name))
          const extras = FALLBACK_PLAYBOOKS.filter(p => p.id?.startsWith('fb-') && !apiIds.has(p.name))
          setPlaybooks([...tagged, ...extras])
        }
      } catch {
        // Keep fallback playbooks
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchPlaybooks()
    return () => { cancelled = true }
  }, [])

  // Track usage when running a playbook
  const handleRun = useCallback((playbook) => {
    onStartChat(playbook.prompt)
    // Fire-and-forget usage tracking
    if (playbook.id && !playbook.id.startsWith('fb-')) {
      const token = localStorage.getItem('amtrend_token')
      fetch(`/api/playbooks/${playbook.id}/run`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => {})
    }
  }, [onStartChat])

  const handleCreated = useCallback((newPlaybook) => {
    setPlaybooks((prev) => [...prev, newPlaybook])
  }, [])

  // Filter out write-dependent playbooks for read-only users
  const visiblePlaybooks = canWrite ? playbooks : playbooks.filter(p => !p.requires_write)
  const sections = groupByCategory(visiblePlaybooks)

  return (
    <div className="p-3 flex flex-col gap-1.5">
      {/* Header with create button */}
      <div className="flex items-center justify-between px-1 mb-1">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {visiblePlaybooks.length} playbooks
        </span>
        <button
          onClick={() => setShowCreate(true)}
          className="p-1 rounded-md text-gray-400 hover:text-amtrend-blue hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
          title="Create personal playbook"
        >
          <Plus size={15} />
        </button>
      </div>

      {sections.map((section) => (
        <div key={section.label}>
          <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1 py-1.5">
            {section.label}
          </div>
          {section.items.map((item) => (
            <button
              key={item.id || item.name}
              onClick={() => handleRun(item)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised hover:border-amtrend-blue/40 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 cursor-pointer transition-all duration-150 mb-1.5 group text-left"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[15px] flex-shrink-0 bg-gray-100 dark:bg-surface-dark-overlay text-gray-600 dark:text-gray-400 group-hover:text-amtrend-blue dark:group-hover:text-blue-400 transition-colors">
                {ICON_MAP[item.icon] ? (() => { const Icon = ICON_MAP[item.icon]; return <Icon size={16} /> })() : (item.icon || <Play size={14} />)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-tight truncate">
                    {item.name}
                  </span>
                  {item.tier && item.tier !== 'system' && (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium leading-none ${TIER_STYLES[item.tier] || TIER_STYLES.system}`}>
                      {item.tier}
                    </span>
                  )}
                  {typeof item.run_count === 'number' && item.run_count > 0 && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                      {item.run_count}x
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
                  {item.description || item.desc}
                </div>
              </div>
              <ChevronRight
                size={14}
                className="text-gray-300 dark:text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </button>
          ))}
        </div>
      ))}

      {showCreate && (
        <CreatePlaybookModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}
